"use client";

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { cn } from "../../lib/utils";
import { menuAnchorPosition, menuAnchorRadius, type MenuAnchor } from "./menuPopover";

export type { MenuAnchor };

interface MenuPopoverProps {
  /** Sichtbar? Der Consumer hält den State (Trigger-Button toggelt ihn). */
  open: boolean;
  /** Wird bei Escape / Outside-Click gerufen. */
  onClose: () => void;
  anchor: MenuAnchor;
  /** `relative`-Wrapper, der Trigger + Popover umschließt (Outside-Click-Grenze). */
  anchorRef: RefObject<HTMLElement | null>;
  /** Breite / Feintuning (z.B. "w-56 -right-6"). */
  className?: string;
  /**
   * Ecken-Rundung überschreiben. Default = Anker-Ableitung (nur die freie Ecke
   * diagonal zum Anker rund). Setzen, wenn das Popup NICHT an beiden anker-nahen
   * Kanten anliegt — dann bleiben nur die tatsächlich harten Kanten (Bildschirmrand /
   * Header-Border) eckig, die freien Ecken folgen dem Theme-Radius. Z.B.
   * `rounded-b-[calc(var(--radius)+2px)]` (nur Oberkante hart) oder
   * `rounded-r-[calc(var(--radius)+2px)]` (nur linke Kante hart).
   */
  roundedClassName?: string;
  children: ReactNode;
}

/**
 * Schwebende Menü-Karte, suite-weit einheitlich: `bg-background` + Border,
 * Spring-Animation (`.jw-pop` aus globals.css), Exit-Animation vor Unmount,
 * Outside-Click + Escape. Positionierung über `anchor`; der randbündige
 * Negativ-Offset kommt kontextabhängig per `className` vom Consumer.
 */
export function MenuPopover({ open, onClose, anchor, anchorRef, className, roundedClassName, children }: MenuPopoverProps) {
  // DOM-Präsenz getrennt vom Sichtbar-Zustand → erlaubt Exit-Animation.
  const [render, setRender] = useState(open);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      setRender(true);
      return;
    }
    if (!render) return;
    // Deterministischer Unmount (reduced-motion feuert kein animationend).
    timer.current = setTimeout(() => setRender(false), 220);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [open, render]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const onOutside = (e: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onOutside); };
  }, [open, onClose, anchorRef]);

  // ── IM FENSTER BLEIBEN ────────────────────────────────────────────────────
  // Der Anker legt nur fest, an welcher Kante die Karte waechst. Steht der Ausloeser
  // nah am Rand (Messenger: „…" neben einer fremden Nachricht am Telefon), lief die
  // 224-px-Karte ueber den rechten Rand hinaus und machte die Seite seitlich rollbar
  // (Betreiber 2026-10-21). Gemessen wird am LAYOUT (offsetLeft/-Width), nicht am
  // Rechteck: `.jw-pop` skaliert beim Auftritt. Verschoben per `translate`, einer
  // eigenen Eigenschaft — `transform` gehoert der Animation.
  const karte = useRef<HTMLDivElement>(null);
  const [versatz, setVersatz] = useState(0);
  useLayoutEffect(() => {
    const el = karte.current;
    const wirt = el?.offsetParent as HTMLElement | null | undefined;
    if (!open || !el || !wirt) return;
    const links = wirt.getBoundingClientRect().left + el.offsetLeft;
    const rechts = links + el.offsetWidth;
    const rand = 8;
    const breite = document.documentElement.clientWidth;
    const dx = rechts > breite - rand ? breite - rand - rechts : links < rand ? rand - links : 0;
    setVersatz(dx);
  }, [open, render]);

  if (!render) return null;

  return (
    <div
      ref={karte}
      style={versatz ? { translate: `${versatz}px 0` } : undefined}
      data-state={open ? "open" : "closed"}
      data-preset="spring"
      data-anchor={anchor}
      onAnimationEnd={(e) => {
        // Substring-Match (NICHT /Out$/): das bottom-left-Close-Keyframe heisst
        // `jwSpringOutUp` (endet auf "Up"). /Out/ trifft beide; der 220ms-Timer
        // ist ohnehin der deterministische Fallback (reduced-motion feuert kein Event).
        if (!open && e.target === e.currentTarget && /Out/i.test(e.animationName)) {
          if (timer.current) clearTimeout(timer.current);
          setRender(false);
        }
      }}
      className={cn(
        // Eckig als Basis; nur Ecken an freien (nicht kantenberührenden) Seiten runden.
        // Default aus dem Anker; Consumer überschreiben, wenn nicht beide anker-nahen
        // Kanten hart sind (Bildschirmrand / Header-Border).
        //
        // ── `bg-popover`, NICHT `bg-background` ─────────────────────────────
        // Ein Menue ist eine ERHEBUNG (E3 der Ebenenleiter), keine Seite. Bis
        // 0.461.1 stand hier `bg-background`, und das hatte zwei Folgen, die
        // beide erst unter einer Schale sichtbar wurden:
        //
        // 1. **Weiss auf Weiss.** Das Konto-Popup liegt IM `<aside>` und erbt
        //    dessen umdefiniertes `--foreground` (auf der Schale hell). Darunter
        //    malte `--background` das Theme-Weiss — unter „umklammert" hell war
        //    der Name damit bei **1,00 : 1**, also schlicht weg. Am laufenden
        //    Showroom gemessen, ueber alle 16 Stellungen identisch: es ist kein
        //    Fehler des eingeklappten Zustands, dort faellt er nur auf.
        // 2. **Keine Erhebung.** `globals.css` haengt die drei uebrigen
        //    E3-Rollen an die KLASSE `.bg-popover` (nicht an eine Marke). Wer
        //    sie nicht traegt, bekommt sie nicht: im Dunkeln stand reines
        //    Schwarz auf der schwarzen Schale, getrennt nur durch einen Rahmen
        //    bei 2,09 : 1.
        //
        // `text-popover-foreground` gehoert dazu — eine Flaeche reist mit ihrem
        // Vordergrund, sonst ist es derselbe Fehler in der Gegenrichtung.
        "jw-pop absolute z-[500] overflow-hidden rounded-none border border-border bg-popover text-popover-foreground shadow-lg data-[state=closed]:pointer-events-none",
        menuAnchorPosition(anchor),
        roundedClassName ?? menuAnchorRadius(anchor),
        className,
      )}
    >
      {children}
    </div>
  );
}
